import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Button, Checkbox, CircularProgress, Chip, IconButton, Tooltip,
} from '@mui/material';
import { OpenInNew, UploadFile, Delete, Print, Receipt, Science, ArrowBack } from '@mui/icons-material';
import TopBar from '../../components/TopBar';
import { insuranceCaseService } from '../../services/api';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const InsuranceDocumentPacketPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const fileInputRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const res = await insuranceCaseService.getPacket(id);
      setPacket(res.data);
    } catch {
      alert('Failed to load document packet');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggleCompleted = async (doc) => {
    setSavingId(doc.documentTypeId);
    try {
      await insuranceCaseService.updateChecklist(id, {
        entries: [{ documentTypeId: doc.documentTypeId, isCompleted: !doc.isCompleted }],
      });
      await load();
    } catch {
      alert('Failed to update checklist');
    } finally {
      setSavingId(null);
    }
  };

  const handleUpload = async (doc, file) => {
    if (!file) return;
    setSavingId(doc.documentTypeId);
    try {
      await insuranceCaseService.uploadDocument(id, doc.id, file);
      await load();
    } catch {
      alert('Failed to upload file');
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteFile = async (doc) => {
    if (!window.confirm('Remove the uploaded file?')) return;
    setSavingId(doc.documentTypeId);
    try {
      await insuranceCaseService.deleteDocumentFile(id, doc.id);
      await load();
    } catch {
      alert('Failed to remove file');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (!packet) {
    return <Box sx={{ p: 4 }}><Typography>Case not found.</Typography></Box>;
  }

  const admissionId = packet.admissionId;

  const renderAction = (doc) => {
    const template = (packet.templates || []).find(t => t.documentTypeId === doc.documentTypeId);

    if (template) {
      return (
        <Button
          size="small" variant="contained" startIcon={<Print />}
          onClick={() => window.open(`/insurance-cases/${id}/documents/${template.id}/print`, '_blank')}
        >
          Generate & Print
        </Button>
      );
    }

    if (doc.documentTypeId === 4) {
      // Discharge Summary
      return packet.dischargeSummaryExists ? (
        <Button size="small" variant="outlined" startIcon={<OpenInNew />}
          onClick={() => navigate(`/ipd/patient/${admissionId}/discharge-summary`)}>
          Open Discharge Summary
        </Button>
      ) : (
        <Typography variant="body2" color="text.secondary">Not created yet</Typography>
      );
    }

    if (doc.documentTypeId === 5) {
      // Itemized Final Bill
      return packet.bill ? (
        <Button size="small" variant="outlined" startIcon={<Receipt />}
          onClick={() => navigate(`/ipd/patient/${admissionId}/billing`)}>
          Open Itemized Bill
        </Button>
      ) : (
        <Typography variant="body2" color="text.secondary">Not generated yet</Typography>
      );
    }

    if (doc.documentTypeId === 6) {
      // Investigation Reports
      return (
        <Button size="small" variant="contained" startIcon={<Science />}
          onClick={() => window.open(`/insurance-cases/${id}/documents/investigations/print`, '_blank')}>
          View / Print
        </Button>
      );
    }

    // Generic file upload
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {doc.fileUrl ? (
          <>
            <Button
              size="small" variant="outlined" startIcon={<OpenInNew />}
              onClick={() => window.open(`${API_BASE}/${doc.fileUrl}`, '_blank')}
            >
              {doc.fileName || 'View File'}
            </Button>
            <Tooltip title="Remove file">
              <IconButton size="small" color="error" disabled={savingId === doc.documentTypeId} onClick={() => handleDeleteFile(doc)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Button
              size="small" variant="outlined" startIcon={<UploadFile />}
              disabled={savingId === doc.documentTypeId}
              onClick={() => fileInputRefs.current[doc.documentTypeId]?.click()}
            >
              Upload
            </Button>
            <input
              type="file" hidden
              ref={el => { fileInputRefs.current[doc.documentTypeId] = el; }}
              onChange={e => handleUpload(doc, e.target.files[0])}
            />
          </>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <TopBar title={`Claim Document Packet — Case #${packet.id}`} onBack={() => navigate(-1)} />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="h6">
              {packet.patient ? `${packet.patient.firstName} ${packet.patient.lastName}` : ''} — {packet.claimType}
            </Typography>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/insurance-desk')}>
              Back to Insurance Desk
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">Insurer: {packet.insurerName || '—'}</Typography>
            <Typography variant="body2" color="text.secondary">TPA: {packet.tpaName || '—'}</Typography>
            <Typography variant="body2" color="text.secondary">Policy: {packet.policyNumber || '—'}</Typography>
            <Typography variant="body2" color="text.secondary">Member ID: {packet.memberId || '—'}</Typography>
            <Chip size="small" label={packet.status} />
          </Box>
        </Paper>

        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={60}>Ready</TableCell>
                <TableCell>Document</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(packet.documents || []).map(doc => (
                <TableRow key={doc.id ?? doc.documentTypeId}>
                  <TableCell>
                    <Checkbox
                      checked={!!doc.isCompleted}
                      disabled={savingId === doc.documentTypeId}
                      onChange={() => toggleCompleted(doc)}
                    />
                  </TableCell>
                  <TableCell>{doc.documentTypeName}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{doc.documentTypeDescription || '—'}</Typography>
                    {doc.uploadedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Uploaded {new Date(doc.uploadedAt).toLocaleString('en-IN')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{renderAction(doc)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Container>
    </Box>
  );
};

export default InsuranceDocumentPacketPage;
